import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Link as LinkIcon, Sparkles, TrendingUp } from 'lucide-react';

interface ProductUploadFormProps {
  onSuccess: () => void;
}

interface PricingConfig {
  duty_percentage: number;
  vat_percentage: number;
}

export const ProductUploadForm = ({ onSuccess }: ProductUploadFormProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingLink, setFetchingLink] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [analyzingSEO, setAnalyzingSEO] = useState(false);
  const [pricingConfig, setPricingConfig] = useState<PricingConfig | null>(null);
  const [seoAnalysis, setSeoAnalysis] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    productLink: '',
    name: '',
    model: '',
    description: '',
    unitPrice: '',
    quantity: '1',
    volume: '',
    images: '',
    listingType: 'retail' as 'retail' | 'b2b_wholesale',
  });

  useEffect(() => {
    fetchPricingConfig();
  }, []);

  const fetchPricingConfig = async () => {
    const { data, error } = await supabase
      .from('pricing_config')
      .select('*')
      .single();

    if (!error && data) {
      setPricingConfig(data);
    }
  };

  const calculateTotalPrice = () => {
    if (!formData.unitPrice || !pricingConfig) return 0;
    
    const unitPrice = parseFloat(formData.unitPrice);
    const quantity = parseInt(formData.quantity) || 1;
    
    const dutyAmount = unitPrice * (pricingConfig.duty_percentage / 100);
    const priceWithDuty = unitPrice + dutyAmount;
    const vatAmount = priceWithDuty * (pricingConfig.vat_percentage / 100);
    const totalPricePerUnit = priceWithDuty + vatAmount;
    
    return totalPricePerUnit * quantity;
  };

  const getPriceBreakdown = () => {
    if (!formData.unitPrice || !pricingConfig) return null;
    
    const unitPrice = parseFloat(formData.unitPrice);
    const quantity = parseInt(formData.quantity) || 1;
    
    const dutyAmount = unitPrice * (pricingConfig.duty_percentage / 100);
    const priceWithDuty = unitPrice + dutyAmount;
    const vatAmount = priceWithDuty * (pricingConfig.vat_percentage / 100);
    const totalPricePerUnit = priceWithDuty + vatAmount;
    
    return {
      unitPrice,
      dutyAmount,
      priceWithDuty,
      vatAmount,
      totalPricePerUnit,
      quantity,
      totalPrice: totalPricePerUnit * quantity
    };
  };

  const handleFetchFromLink = async () => {
    if (!formData.productLink) {
      toast.error('Please enter a product link');
      return;
    }

    setFetchingLink(true);
    toast.info('Auto-fetch feature coming soon. Please enter details manually for now.');
    setFetchingLink(false);
  };

  const handleGenerateDescription = async () => {
    if (!formData.name) {
      toast.error('Please enter a product name first');
      return;
    }

    setGeneratingAI(true);
    try {
      const { data, error } = await supabase.functions.invoke('product-seo-analyzer', {
        body: {
          action: 'generate_description',
          productData: {
            name: formData.name,
            model: formData.model,
            description: formData.description
          }
        }
      });

      if (error) throw error;

      setFormData({ ...formData, description: data.description });
      toast.success('AI-generated description added!');
    } catch (error: any) {
      console.error('Generate description error:', error);
      toast.error(error.message || 'Failed to generate description');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleAnalyzeSEO = async () => {
    if (!formData.name || !formData.description) {
      toast.error('Please fill in name and description first');
      return;
    }

    setAnalyzingSEO(true);
    try {
      const { data, error } = await supabase.functions.invoke('product-seo-analyzer', {
        body: {
          action: 'analyze_seo',
          productData: {
            name: formData.name,
            model: formData.model,
            description: formData.description,
            unitPrice: formData.unitPrice,
            images: formData.images ? formData.images.split(',').map(url => url.trim()) : []
          }
        }
      });

      if (error) throw error;

      setSeoAnalysis(data);
      toast.success(`SEO Score: ${data.total_score}/100`);
    } catch (error: any) {
      console.error('SEO analysis error:', error);
      toast.error(error.message || 'Failed to analyze SEO');
    } finally {
      setAnalyzingSEO(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    if (!formData.name || !formData.unitPrice) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);

    const totalPrice = calculateTotalPrice();
    const imagesArray = formData.images ? formData.images.split(',').map(url => url.trim()) : [];

    // Auto-approve if score >= 80
    const autoApproveStatus = seoAnalysis && seoAnalysis.total_score >= 80 ? 'approved' : 'pending';
    
    const { error } = await supabase.from('products').insert({
      agent_id: user.id,
      name: formData.name,
      model: formData.model || null,
      description: formData.description || null,
      unit_price: parseFloat(formData.unitPrice),
      quantity: parseInt(formData.quantity),
      volume: formData.volume ? parseFloat(formData.volume) : null,
      images: imagesArray,
      product_link: formData.productLink || null,
      total_price: totalPrice,
      listing_type: formData.listingType,
      seo_score: seoAnalysis?.total_score || null,
      seo_keywords: seoAnalysis?.keywords || null,
      status: autoApproveStatus
    });

    setLoading(false);

    if (error) {
      toast.error('Failed to submit product');
      console.error(error);
    } else {
      const message = autoApproveStatus === 'approved' 
        ? 'Product auto-approved! (Score: 80+)' 
        : 'Product submitted for review';
      toast.success(message);
      onSuccess();
    }
  };

  const priceBreakdown = getPriceBreakdown();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="flex gap-2">
          <div className="flex-1 space-y-2">
            <Label htmlFor="productLink">Product Link (Optional)</Label>
            <Input
              id="productLink"
              type="url"
              placeholder="https://..."
              value={formData.productLink}
              onChange={(e) => setFormData({ ...formData, productLink: e.target.value })}
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleFetchFromLink}
              disabled={fetchingLink || !formData.productLink}
            >
              {fetchingLink ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LinkIcon className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Product Name *</Label>
          <Input
            id="name"
            required
            placeholder="e.g., Luna M9 Smart Toilet"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="model">Model</Label>
          <Input
            id="model"
            placeholder="e.g., M9-2024"
            value={formData.model}
            onChange={(e) => setFormData({ ...formData, model: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="description">Description</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerateDescription}
              disabled={generatingAI || !formData.name}
            >
              {generatingAI ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              AI Generate
            </Button>
          </div>
          <Textarea
            id="description"
            placeholder="Product description..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="unitPrice">Unit Price (€) *</Label>
            <Input
              id="unitPrice"
              type="number"
              step="0.01"
              required
              placeholder="0.00"
              value={formData.unitPrice}
              onChange={(e) => setFormData({ ...formData, unitPrice: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="quantity">Quantity *</Label>
            <Input
              id="quantity"
              type="number"
              required
              min="1"
              placeholder="1"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="listingType">Listing Type</Label>
          <Select value={formData.listingType} onValueChange={(value: 'retail' | 'b2b_wholesale') => setFormData({ ...formData, listingType: value })}>
            <SelectTrigger id="listingType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="retail">Retail</SelectItem>
              <SelectItem value="b2b_wholesale">B2B Wholesale</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="volume">Volume (m³)</Label>
          <Input
            id="volume"
            type="number"
            step="0.001"
            placeholder="0.000"
            value={formData.volume}
            onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="images">Image URLs (comma-separated)</Label>
          <Textarea
            id="images"
            placeholder="https://image1.jpg, https://image2.jpg"
            value={formData.images}
            onChange={(e) => setFormData({ ...formData, images: e.target.value })}
            rows={2}
          />
        </div>

        <Button
          type="button"
          variant="secondary"
          onClick={handleAnalyzeSEO}
          disabled={analyzingSEO || !formData.name || !formData.description}
          className="w-full"
        >
          {analyzingSEO ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <TrendingUp className="mr-2 h-4 w-4" />
              Analyze SEO & Content Quality
            </>
          )}
        </Button>
      </div>

      {seoAnalysis && (
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">SEO Analysis</h3>
              <Badge 
                variant={seoAnalysis.total_score >= 80 ? "default" : seoAnalysis.total_score >= 60 ? "secondary" : "destructive"}
                className="text-lg px-3 py-1"
              >
                {seoAnalysis.total_score}/100
              </Badge>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>SEO Optimization:</span>
                <span className="font-medium">{seoAnalysis.seo_score}/40</span>
              </div>
              <div className="flex justify-between">
                <span>Content Completeness:</span>
                <span className="font-medium">{seoAnalysis.completeness_score}/30</span>
              </div>
              <div className="flex justify-between">
                <span>Description Quality:</span>
                <span className="font-medium">{seoAnalysis.quality_score}/30</span>
              </div>
            </div>

            {seoAnalysis.keywords && seoAnalysis.keywords.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-sm font-medium mb-2">Suggested Keywords:</p>
                <div className="flex flex-wrap gap-2">
                  {seoAnalysis.keywords.map((keyword: string, idx: number) => (
                    <Badge key={idx} variant="outline">{keyword}</Badge>
                  ))}
                </div>
              </div>
            )}

            {seoAnalysis.feedback && (
              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground">{seoAnalysis.feedback}</p>
              </div>
            )}

            {seoAnalysis.total_score >= 80 && (
              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-green-600 dark:text-green-400">
                  ✓ This product will be auto-approved upon submission!
                </p>
              </div>
            )}
            {seoAnalysis.total_score < 60 && (
              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-orange-600 dark:text-orange-400">
                  ⚠ Score below 60 - Consider improving content before submission
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {priceBreakdown && pricingConfig && (
        <Card className="bg-muted/50">
          <CardContent className="pt-6 space-y-2">
            <h3 className="font-semibold mb-3">Price Breakdown</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Unit Price:</span>
                <span>€{priceBreakdown.unitPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>+ Duty ({pricingConfig.duty_percentage}%):</span>
                <span>€{priceBreakdown.dutyAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>+ VAT ({pricingConfig.vat_percentage}%):</span>
                <span>€{priceBreakdown.vatAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-medium pt-2 border-t">
                <span>Price per Unit:</span>
                <span>€{priceBreakdown.totalPricePerUnit.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>× Quantity:</span>
                <span>{priceBreakdown.quantity}</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>Total Price:</span>
                <span>€{priceBreakdown.totalPrice.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Button type="submit" disabled={loading} className="w-full">
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          'Submit for Review'
        )}
      </Button>
    </form>
  );
};